"use client";

import {useQuery} from "@tanstack/react-query";
import {useEffect, useState} from "react";

import {MDXRemote, MDXRemoteSerializeResult} from "next-mdx-remote";
import {serialize} from "next-mdx-remote/serialize";

import DownloadLink from "@/components/ui/download-link";
import FetchError from "@/components/ui/fetch-error";
import Icon from "@/components/ui/icon";
import Loading from "@/components/ui/loading";
import {FileProps} from "@/types";
import {Button, Modal, Table} from "react-bootstrap";

import api from "@/lib/api";

const headers = [
  "HARMONIZED DATASET",
  "CURRENT VERSIONS",
  "LINK TO DOWNLOAD HARMONIZED DATASET",
  "DATA ACCESS INSTRUCTIONS",
  "HARMONIZED CODEBOOK",
];

type DatasetEntry = {
  value: number;
  label: string;
  version: string | null;
  url: string | null;
  instruction: {
    content: string;
    files: FileProps[];
  } | null;
  codebook: FileProps | null;
};

export default function GetHarmonizedDataTable() {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>();
  const [parsedMdx, setParsedMdx] = useState<MDXRemoteSerializeResult>();
  const [selectedStudy, setSelectedStudy] = useState<string>();

  const {
    data = [],
    error,
    isPending,
  } = useQuery({
    queryKey: ["hrd-get-data", "covid"],
    queryFn: async () => {
      const params = new URLSearchParams({site_name: "covid"});
      return await api.get<DatasetEntry[]>(
        `/hrd/get_data/?${params.toString()}`
      );
    },
  });

  // Parse MDX content for modal
  useEffect(() => {
    const parseMdx = async () => {
      try {
        if (modalContent) {
          const mdxSource = await serialize(modalContent, {
            mdxOptions: {
              remarkPlugins: [],
              rehypePlugins: [],
            },
          });
          setParsedMdx(mdxSource);
        }
      } catch (error) {
        console.error("Failed to parse MDX:", error);
      }
    };

    parseMdx();
  }, [modalContent]);

  const processInstructionContent = (content: string, files: FileProps[]) => {
    let updatedContent = content;
    files.forEach((file) => {
      const regex = new RegExp(`!\\[.*?\\]\\(${file.label}\\)`, "g");
      updatedContent = updatedContent.replace(regex, `![](${file.url})`);
    });
    return updatedContent;
  };

  // Show modal with instruction content
  const handleShowModal = (
    study_name: string,
    instruction: {content: string; files: FileProps[]}
  ) => {
    const processedContent = processInstructionContent(
      instruction.content,
      instruction.files || []
    );
    setModalContent(processedContent);
    setSelectedStudy(study_name);
    setShowModal(true);
  };

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  return (
    <>
      {data.length > 0 && (
        <div className='mb-4 mt-4'>
          <Table striped responsive>
            <thead>
              <tr className='g2-table-header-blue'>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={`${item.label}_${item.value}`}>
                  <td>{item.label}</td>
                  <td>{item.version || "-"}</td>
                  <td>
                    {item.url ? (
                      (() => {
                        const match = item.url.match(
                          /^\s*\[([^\]]+)\]\(([^)]+)\)\s*$/
                        );

                        const href = match ? match[2] : item.url;
                        const text = match ? match[1] : item.label;

                        return (
                          <a
                            href={href}
                            target='_blank'
                            className='g2-link-blue fw-semibold'
                          >
                            {text}
                          </a>
                        );
                      })()
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td>
                    {item.instruction ? (
                      <span
                        className='g2-link-blue'
                        onClick={() =>
                          handleShowModal(item.label, item.instruction!)
                        }
                        style={{cursor: "pointer"}}
                      >
                        <Icon icon='box-arrow-up-right' ms={2} />
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td>
                    {item.codebook ? (
                      <DownloadLink file={item.codebook}>
                        <Icon icon='file-earmark-pdf' ms={2} />
                      </DownloadLink>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size='xl'
        aria-labelledby='hrd-instruction-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedStudy}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{parsedMdx && <MDXRemote {...parsedMdx} />}</Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
